import React, { useLayoutEffect, useState } from 'react';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import Input from '../../forms/Input';
import Checkbox from '../../forms/Checkbox';
import { useGlobalState } from '../../stateProvider/StateProvider';
import Button from '../../forms/Button';
import RoleProtected, { userRoles } from '../../RoleProtected';
import Table from '../../table/Table';
import { generateId, translate, uniqueSet } from '../../utility/functions';
import GetState from '../../GetState';
import Form from '../../forms/Form';
import { Regex } from '../../utility/regex';
import EmailInput from '../../forms/formComponents/EmailInput';
import ManagementRow from '../../table/ManagementRow';
import Audit from '../User/Audit';
import Col from '../../layout/Col';
import EventLog from './EventLog';
import ModalFactory from '../../Modal';

const UserManagement = ({ ...props }) => {
    const {
        state: {
            active_user,
        },
        updateAccessToken,
        getUserData
    } = useGlobalState();

    const [user, refreshUser] = GetState(QuestRoute.userManagement.list);
    const [roles] = GetState(QuestRoute.userManagement.roles);

    const [userLocations, setUserLocations] = useState([])

    useLayoutEffect(() => {
        if (!active_user.roles.includes(userRoles.admin)) return;
        HamQuest.get(QuestRoute.locations.list)
            .then(res => setUserLocations(res))
    }, [])

    const deleteUser = (user) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${user.firstName} ${user.lastName}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting User',
                body: `Deleting ${user.firstName} ${user.lastName}`,
                callback: () => HamQuest.delete(QuestRoute.userManagement.edit(user.userId))
            },
            confirmed: {
                title: 'Success',
                body: `${user.firstName} ${user.lastName} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshUser()
        })
    }

    const resetUserPassword = (user) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to reset ${user.firstName} ${user.lastName}'s password?`,
                type: 'warning',
            },
            promise: {
                title: 'Resetting password',
                body: `Resetting ${user.firstName} ${user.lastName}'s password.`,
                callback: () => HamQuest.get(QuestRoute.userManagement.resetPassword(user.userId))
            },
            confirmed: {
                title: 'Success',
                body: `An email has been sent to ${user.email} with instructions to reset their password.`,
                type: 'success'
            }
        })
    }

    const addNewUser = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add a new User`,
            body: (
                <Form
                    inputs={{
                        user: '',
                        pass: '',
                        confirmPass: '',
                        firstName: '',
                        lastName: '',
                        roles: [],
                        locations: []
                    }}
                    onSubmit={e => HamQuest.post(QuestRoute.userManagement.list, { ...e })
                        .then(() => {
                            refreshUser();
                            ModalFactory.close(id);
                        })
                    }
                    text='Create'
                    autoComplete='off'
                    name='userManagement'
                >
                    {({ values, setValue }) =>

                        <>
                            <Input
                                label='User Name / Email'
                                name='user'
                                validate={Regex.match.email}
                                required
                            />
                            <Input
                                label='Password'
                                name='pass'
                                type='password'
                                autocomplete='off'
                                validate={Regex.test.password}
                                required
                            />
                            <Input
                                label='Confirm Password'
                                name='confirmPass'
                                type='password'
                                autocomplete='off'
                                confirmation='pass'
                                required
                            />

                            <Input
                                label='First Name'
                                name='firstName'
                                sanitize={Regex.sanitize.name}
                                required
                            />
                            <Input
                                label='Last Name'
                                name='lastName'
                                sanitize={Regex.sanitize.name}
                                required
                            />

                            <b>Roles</b>
                            <Button
                                onClick={() => setValue('roles', values.roles.length === roles.length ? [] : roles.map(e => e.name))}
                                label={values.roles.length === roles.length ? 'Remove All Roles' : 'Add All Roles'}
                            />
                            {
                                roles.map((e, i) => (
                                    <Checkbox
                                        key={'allowedLocations' + i}
                                        name={'roles'}

                                        label={e.name}
                                        value={e.name}
                                    />
                                ))
                            }

                            <RoleProtected roles={userRoles.admin}>
                                <b>Viewable Locations</b>
                                {
                                    userLocations.map((e, i) => (
                                        <Checkbox
                                            key={'allowedLocations' + i}
                                            name='locations'

                                            label={translate(e.label)}
                                            value={e.value}
                                        />
                                    ))
                                }
                            </RoleProtected>
                        </>
                    }
                </Form>
            )
        })
    }

    const editUser = (user) => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Editing ${user.firstName}`,
            body: (
                <Form
                    inputs={{
                        ...user,
                    }}
                    onSubmit={e => HamQuest.put(QuestRoute.userManagement.edit(user.userId), { ...e })
                        .then(() => {
                            refreshUser();
                            updateAccessToken();
                            getUserData();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                    name='userManagement'
                >
                    {({ values, setValue }) =>
                        <>
                            <EmailInput
                                label='User Name / Email'
                            />

                            <Input
                                label='First Name'
                                name='firstName'
                                sanitize={Regex.sanitize.name}
                                required
                            />
                            <Input
                                label='Last Name'
                                name='lastName'
                                sanitize={Regex.sanitize.name}
                                required
                            />

                            <b>Roles</b>
                            <Button
                                onClick={() => setValue('roles', values.roles.length === roles.length ? [] : roles.map(e => e.name))}
                                label={values.roles.length === roles.length ? 'Remove All Roles' : 'Add All Roles'}
                            />
                            {
                                roles.map((e, i) => (
                                    <Checkbox
                                        key={'roles' + i}
                                        name={'roles'}

                                        callback={(val) => {
                                            if (!val) return;
                                            if (e.name.includes('Edit')) {
                                                const role = e.name.replace('Edit', '').trim();
                                                const otherRole = roles.find(f => f.name.includes(role) && !f.name.includes('Edit'));
                                                if (otherRole) {
                                                    setValue('roles', uniqueSet([...values.roles, otherRole.name, e.name]));
                                                }
                                            }
                                        }}

                                        label={e.name}
                                        value={e.name}
                                    />
                                ))
                            }

                            <RoleProtected roles={userRoles.admin}>
                                <b>Viewable Locations</b>
                                {
                                    userLocations.map((e, i) => (
                                        <Checkbox
                                            key={'allowedLocations' + i}
                                            name='locations'

                                            label={translate(e.label)}
                                            value={e.value}
                                        />
                                    ))
                                }
                            </RoleProtected>
                        </>
                    }
                </Form>
            )
        })
    }

    const config = {
        label: 'User Management',
        data: user,
        controls: [<Button onClick={addNewUser} key='AddNew'>Add New</Button>],
        // disableHeaderSearch: true,
        headers: [
            {
                label: 'User',
                propKey: 'firstName',
            }, {
                label: 'Email',
                propKey: 'email',
            },
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={[
                    <Button onClick={() => editUser(e)} key={generateId()}>Edit</Button>,
                    <Button onClick={() => deleteUser(e)} key={generateId()}>Delete</Button>,
                    <Button onClick={() => resetUserPassword(e)} key={generateId()} style={{ whiteSpace: 'nowrap' }}>Reset Password</Button>,
                    <Button to={`/audit/${e.userId}`} key={generateId()} roles={userRoles.admin}>Audit</Button>,
                ]}
            >
                <td>{e.firstName} {e.lastName}</td>
                <td>{e.email}</td>
            </ManagementRow>
        )
    }
    return (
        <Table config={config} />
    );
}

export default UserManagement;